import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/client';
import { useAuth } from '../AdminRoutes';
import {
  useApi, useDebounced, formatDate, timeAgo, humanise,
} from '../hooks/useApi';
import {
  Card, StatusPill, ChannelTag, Pager, EmptyState, ErrorState,
  TableSkeleton, Drawer,
} from '../components/ui';

const PIPELINE = [
  'submitted', 'screening', 'shortlisted',
  'interview_1', 'interview_2',
  'selected', 'offered', 'accepted', 'rejected', 'withdrawn',
];

// Whether a placement is paid is settled after selection, not advertised.
const STIPEND = [
  ['undecided', 'Not decided yet'],
  ['paid', 'Paid'],
  ['unpaid', 'Unpaid'],
];

const ROUTE_LABEL = {
  internship: 'Internship',
  training_internship: 'Training + Internship',
};

export default function Applications() {
  const { canWrite } = useAuth();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [track, setTrack] = useState('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  const q = useDebounced(search);

  const params = useMemo(() => ({
    page, limit: 25,
    q: q || undefined,
    status: status === 'all' ? undefined : status,
    track: track === 'all' ? undefined : track,
  }), [page, q, status, track]);

  const list = useApi('/api/admin/applications', params);
  const detail = useApi(`/api/admin/applications/${openId}`, null, { enabled: Boolean(openId) });
  const tracks = useApi('/api/admin/tracks');

  const setFilter = (setter) => (value) => { setter(value); setPage(1); };

  const patch = async (id, body, message) => {
    try {
      await api.patch(`/api/admin/applications/${id}`, body);
      toast.success(message);
      list.reload();
      if (openId === id) detail.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  /**
   * Resumes are behind auth, so a plain link would 401. Fetch as a blob with
   * the bearer token attached, then hand the browser an object URL.
   */
  const downloadResume = async (record) => {
    try {
      const res = await api.get(`/api/admin/applications/${record.id}/resume`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = record.resume_filename || `${record.first_name}-resume.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('That resume could not be downloaded. It may be missing from storage.');
    }
  };

  const record = detail.data;
  const summary = list.meta?.summary;

  return (
    <div className="spa-stack">
      {summary && (
        <div className="spa-metrics">
          <div className="spa-metric">
            <div className="spa-metric__label">Matching</div>
            <div className="spa-metric__value">{summary.total}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Awaiting screening</div>
            <div className="spa-metric__value" style={{ color: '#175cd3' }}>{summary.submitted_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Shortlisted</div>
            <div className="spa-metric__value">{summary.shortlisted_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">In interview</div>
            <div className="spa-metric__value" style={{ color: '#b54708' }}>{summary.interview_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Selected</div>
            <div className="spa-metric__value">{summary.selected_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Accepted</div>
            <div className="spa-metric__value" style={{ color: '#067647' }}>{summary.accepted_count}</div>
          </div>
        </div>
      )}

      <div className="spa-toolbar">
        <div className="spa-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            className="spa-input" type="search"
            placeholder="Search name, email, college or reference"
            value={search}
            onChange={(e) => setFilter(setSearch)(e.target.value)}
            aria-label="Search applications"
          />
        </div>

        <select className="spa-select" value={status}
          onChange={(e) => setFilter(setStatus)(e.target.value)} aria-label="Filter by stage">
          <option value="all">All stages</option>
          {PIPELINE.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
        </select>

        <select className="spa-select" value={track}
          onChange={(e) => setFilter(setTrack)(e.target.value)} aria-label="Filter by track">
          <option value="all">All tracks</option>
          {(tracks.data || []).map((t) => (
            <option key={t.slug} value={t.slug}>{t.title}</option>
          ))}
        </select>

        <button type="button" className="spa-btn spa-btn--sm" onClick={list.reload}>
          <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Refresh
        </button>
      </div>

      <Card padded={false}>
        {list.error ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : list.isLoading && !list.data ? (
          <TableSkeleton cols={7} />
        ) : !list.data?.length ? (
          <EmptyState
            icon="fa-graduation-cap"
            title="No applications match these filters"
            text="Applications submitted from the Training & Internship page appear here."
          />
        ) : (
          <>
            <div className="spa-tablewrap">
              <table className="spa-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Route</th>
                    <th>Track</th>
                    <th>College</th>
                    <th className="num">CGPA</th>
                    <th>Channel</th>
                    <th>Stage</th>
                    <th className="num">Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((a) => (
                    <tr key={a.id} className="is-clickable" onClick={() => setOpenId(a.id)}>
                      <td>
                        <div className="spa-table__primary">{a.first_name} {a.last_name}</div>
                        <div className="spa-table__sub spa-num">{a.reference_code}</div>
                      </td>
                      <td>
                        <span className={`spa-pill spa-pill--${
                          a.programme_type === 'training_internship' ? 'blue' : 'neutral'}`}>
                          {ROUTE_LABEL[a.programme_type] || 'Internship'}
                        </span>
                      </td>
                      <td>
                        <div>{a.track_title}</div>
                        <div className="spa-table__sub">{humanise(a.work_mode || '')}</div>
                      </td>
                      <td style={{ maxWidth: 190 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.college}
                        </div>
                        <div className="spa-table__sub">{a.branch} · {a.graduation_year}</div>
                      </td>
                      <td className="num">{a.cgpa ?? '—'}</td>
                      <td><ChannelTag channel={a.channel} /></td>
                      <td><StatusPill status={a.status} /></td>
                      <td className="num" title={formatDate(a.created_at, { withTime: true })}>
                        {timeAgo(a.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager pagination={list.meta?.pagination} onPage={setPage} />
          </>
        )}
      </Card>

      {/* -------------------------------------------------------- drawer */}
      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title={record ? `${record.first_name} ${record.last_name}` : 'Loading…'}
        subtitle={record?.reference_code}
        footer={record && (
          <>
            {record.resume_path && (
              <button type="button" className="spa-btn spa-btn--primary"
                onClick={() => downloadResume(record)}>
                <i className="fa-solid fa-download" aria-hidden="true" /> Resume
              </button>
            )}
            <a className="spa-btn" href={`mailto:${record.email}?subject=Your Starparth internship application (${record.reference_code})`}>
              <i className="fa-solid fa-envelope" aria-hidden="true" /> Email
            </a>
            {canWrite && (
              <select
                className="spa-select"
                value={record.status}
                onChange={(e) => patch(record.id, { status: e.target.value },
                  `Moved to ${humanise(e.target.value).toLowerCase()}.`)}
                aria-label="Change stage"
              >
                {PIPELINE.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
              </select>
            )}
          </>
        )}
      >
        {!record ? (
          <TableSkeleton rows={6} cols={2} />
        ) : (
          <>
            <div className="spa-section-label">Candidate</div>
            <dl className="spa-dl">
              <dt>Email</dt><dd><a href={`mailto:${record.email}`}>{record.email}</a></dd>
              <dt>Phone</dt><dd><a href={`tel:${record.phone}`}>{record.phone}</a></dd>
              <dt>Location</dt><dd>{[record.city, record.state].filter(Boolean).join(', ') || '—'}</dd>
              <dt>Stage</dt><dd><StatusPill status={record.status} /></dd>
            </dl>

            <div className="spa-section-label">Education</div>
            <dl className="spa-dl">
              <dt>College</dt><dd>{record.college}</dd>
              <dt>Course</dt><dd>{[record.degree, record.branch].filter(Boolean).join(' — ') || '—'}</dd>
              <dt>Graduating</dt><dd className="spa-num">{record.graduation_year || '—'}</dd>
              <dt>CGPA</dt><dd className="spa-num">{record.cgpa ?? '—'}</dd>
            </dl>

            <div className="spa-section-label">Programme</div>
            <dl className="spa-dl">
              <dt>Route</dt><dd>{ROUTE_LABEL[record.programme_type] || 'Internship'}</dd>
              <dt>Track</dt><dd>{record.track_title}</dd>
              <dt>Work mode</dt><dd>{humanise(record.work_mode || '—')}</dd>
              <dt>Preferred start</dt><dd>{formatDate(record.preferred_start)}</dd>
              <dt>Heard via</dt><dd>{record.heard_from || '—'}</dd>
            </dl>

            {record.skills?.length > 0 && (
              <>
                <div className="spa-section-label">Skills claimed</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {record.skills.map((s) => (
                    <span key={s} className="spa-pill spa-pill--neutral">{s}</span>
                  ))}
                </div>
              </>
            )}

            {(record.linkedin_url || record.github_url || record.portfolio_url) && (
              <>
                <div className="spa-section-label">Links</div>
                <dl className="spa-dl">
                  {record.linkedin_url && (<><dt>LinkedIn</dt><dd>
                    <a href={record.linkedin_url} target="_blank" rel="noreferrer">Open</a></dd></>)}
                  {record.github_url && (<><dt>GitHub</dt><dd>
                    <a href={record.github_url} target="_blank" rel="noreferrer">Open</a></dd></>)}
                  {record.portfolio_url && (<><dt>Portfolio</dt><dd>
                    <a href={record.portfolio_url} target="_blank" rel="noreferrer">Open</a></dd></>)}
                </dl>
              </>
            )}

            {record.cover_note && (
              <>
                <div className="spa-section-label">Why this track</div>
                <div className="spa-quote">{record.cover_note}</div>
              </>
            )}

            <div className="spa-section-label">Attribution</div>
            <dl className="spa-dl">
              <dt>Channel</dt><dd><ChannelTag channel={record.channel} /></dd>
              {record.utm_campaign && (<><dt>Campaign</dt><dd>{record.utm_campaign}</dd></>)}
              <dt>Applied</dt><dd>{formatDate(record.created_at, { withTime: true })}</dd>
            </dl>

            {canWrite && (
              <>
                <div className="spa-section-label">Stipend decision</div>
                <p className="spa-card__note" style={{ marginBottom: 10 }}>
                  Set once the candidate is selected. This is not shown on the public page.
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {STIPEND.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`spa-btn spa-btn--sm${
                        record.stipend_status === value ? ' spa-btn--primary' : ''}`}
                      onClick={() => patch(record.id, { stipend_status: value },
                        `Stipend marked as ${label.toLowerCase()}.`)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {record.stipend_status === 'paid' && (
                  <StipendAmount
                    key={record.id}
                    initial={record.stipend_amount || ''}
                    onSave={(v) => patch(record.id, { stipend_amount: v }, 'Stipend amount saved.')}
                  />
                )}

                <div className="spa-section-label">Your assessment</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`spa-btn spa-btn--sm${record.rating === n ? ' spa-btn--primary' : ''}`}
                      onClick={() => patch(record.id, { rating: n }, `Rated ${n} out of 5.`)}
                      aria-label={`Rate ${n} out of 5`}
                    >
                      {n}
                    </button>
                  ))}
                  <span style={{ alignSelf: 'center', fontSize: '0.8rem', color: '#667085' }}>
                    {record.reviewer_name ? `Last reviewed by ${record.reviewer_name}` : 'Not yet rated'}
                  </span>
                </div>
                <ReviewNotes
                  key={record.id}
                  initial={record.reviewer_notes || ''}
                  onSave={(text) => patch(record.id, { reviewer_notes: text }, 'Notes saved.')}
                />
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

function StipendAmount({ initial, onSave }) {
  const [value, setValue] = useState(initial);
  return (
    <div className="spa-row" style={{ marginBottom: 4 }}>
      <input
        className="spa-input"
        style={{ maxWidth: 220 }}
        placeholder="e.g. Rs 10,000 / month"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="button" className="spa-btn spa-btn--sm"
        disabled={value === initial} onClick={() => onSave(value)}>
        Save
      </button>
    </div>
  );
}

function ReviewNotes({ initial, onSave }) {
  const [text, setText] = useState(initial);
  const dirty = text !== initial;

  return (
    <div>
      <textarea
        className="spa-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What stood out, and what to probe in the call."
      />
      <button
        type="button"
        className="spa-btn spa-btn--sm spa-btn--primary"
        style={{ marginTop: 8 }}
        disabled={!dirty}
        onClick={() => onSave(text)}
      >
        {dirty ? 'Save notes' : 'Saved'}
      </button>
    </div>
  );
}
